import React, { useEffect } from 'react';
import { format } from 'date-fns';
import TitleBlock from '@/modules/topic/upsert/TitleBlock';
import TopicBasicsBlock from '@/modules/topic/upsert/TopicBasicsBlock';
import VisibilityLimitsBlock from '@/modules/topic/upsert/VisibilityLimitsBlock';
import CreateFooter from '@/modules/topic/upsert/CreateFooter';
import { pageHeaderTitleStyle } from '@/modules/topic/read/styles';
import {
  centeredHeaderStyle,
  editBodyStyle,
  pageHeaderRowStyle,
} from '@/modules/topic/upsert/constants';
import type { TopicEditBodyProps } from '../types';
import SharingSetupLink from '../SharingSetupLink';
import { useUserContext } from '@/modules/user/useUserContext';
import SharingSetup from './SharingSetup';
import EditableBylineDatelineBlock from './EditableBylineDatelineBlock';

/**
 * News create/edit body — composed inline from the shared editable sections so the
 * news form can diverge from the other types over time. It is the structurally
 * distinct type: its title and body read as a newspaper's headline and
 * article, `VisibilityLimitsBlock` renders the impression-goal field for
 * `type="news"`, and only news has the Byline & Dateline card, whose date and time
 * are the topic's start (news has no "When" card). Topic type is fixed
 * (no type-selection UI); the Sharing card sits last, above
 * the create button on create, so the sharing form can be edited before the
 * topic first saves.
 */
const EditForm: React.FC<TopicEditBodyProps> = ({ upsert, isCreateMode }) => {
  const {
    topicTitle,
    setTopicTitle,
    topicImageData,
    setTopicImageData,
    handleSelectTopicImage,
    topicBody,
    setTopicBody,
    startTime,
    setStartTime,
    timezone,
    setTimezone,
    timezones,
    endBeforeStart,
    byInviteOnly,
    setByInviteOnly,
    introLimitCountEnabled,
    setIntroLimitCountEnabled,
    introLimitCount,
    setIntroLimitCount,
    globalIntroMax,
    impressionGoal,
    setImpressionGoal,
    onlyManagersCanShare,
    setOnlyManagersCanShare,
    saving,
    canCreateTopics,
    createTopic,
    sharingSetup,
    type,
    byline,
    setByline,
    location,
    setLocation,
  } = upsert;
  const { familiarName } = useUserContext();

  // A new story starts out credited to the author's familiar name and dated
  // today, with no time of day. The create form resets to type 'event' on
  // mount and is then given the 'news' type, so seeding on that change runs
  // after the reset.
  useEffect(() => {
    if (isCreateMode && type === 'news') {
      setByline(familiarName ?? '');
      setStartTime(format(new Date(), "yyyy-MM-dd'T'00:00:00"));
    }
    // Seed once per arrival at the news type, not on every name edit.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isCreateMode, type, setByline, setStartTime]);

  return (
    <>
      <div style={centeredHeaderStyle}>
        <div style={pageHeaderRowStyle}>
          <h1 style={pageHeaderTitleStyle}>
            {isCreateMode ? 'New News Story' : 'Edit News Story'}
          </h1>
        </div>
      </div>

      <div style={editBodyStyle}>
        <TopicBasicsBlock
          titleBlock={
            <TitleBlock
              topicTitle={topicTitle}
              setTopicTitle={setTopicTitle}
              label="Headline"
              placeholder="Enter headline"
            />
          }
          imageData={topicImageData}
          onSelectFile={handleSelectTopicImage}
          onClearImage={() => setTopicImageData(null)}
          topicBody={topicBody}
          setTopicBody={setTopicBody}
          bodyLabel="Article"
          bodyPlaceholder="Enter article (markdown supported)"
        />
        <EditableBylineDatelineBlock
          byline={byline}
          setByline={setByline}
          location={location}
          setLocation={setLocation}
          startTime={startTime}
          setStartTime={setStartTime}
          timezone={timezone}
          setTimezone={setTimezone}
          timezones={timezones}
        />
        <VisibilityLimitsBlock
          byInviteOnly={byInviteOnly}
          setByInviteOnly={setByInviteOnly}
          introLimitCountEnabled={introLimitCountEnabled}
          setIntroLimitCountEnabled={setIntroLimitCountEnabled}
          introLimitCount={introLimitCount}
          setIntroLimitCount={setIntroLimitCount}
          globalIntroMax={globalIntroMax}
          impressionGoal={impressionGoal}
          setImpressionGoal={setImpressionGoal}
          onlyManagersCanShare={onlyManagersCanShare}
          setOnlyManagersCanShare={setOnlyManagersCanShare}
        />
        <SharingSetupLink SharingSetup={SharingSetup} draft={sharingSetup} />
        {isCreateMode && (
          <CreateFooter
            endBeforeStart={endBeforeStart}
            saving={saving}
            topicTitle={topicTitle}
            canCreateTopics={canCreateTopics}
            onCreate={createTopic}
          />
        )}
      </div>
    </>
  );
};

export default EditForm;
